/** Category name for Joint Venture deal questionnaire (always re-sendable). */
export const JV_DEAL_CATEGORY_NAME = "JV Deal";

export const isJvDealCategoryName = (categoryName) =>
  String(categoryName || "")
    .trim()
    .toLowerCase() === JV_DEAL_CATEGORY_NAME.toLowerCase();

/** Collect question IDs that belong to the JV Deal category (never lock as already-sent). */
export const collectJvDealQuestionIds = (categories = [], mappedQuestions = []) => {
  const ids = new Set();
  (categories || []).forEach((category) => {
    if (!isJvDealCategoryName(category?.categoryName)) return;
    category?.questions?.forEach((question) => {
      if (question?.questionId) ids.add(question.questionId);
    });
  });
  (mappedQuestions || []).forEach((question) => {
    if (
      question?.questionId &&
      isJvDealCategoryName(question.categoryName)
    ) {
      ids.add(question.questionId);
    }
  });
  return ids;
};

/** JV Deal questions already captured in enquiry → first deal upload (do not re-ask). */
export const JV_ENQUIRY_OVERLAP_QUESTION_TITLES = [
  "Please specify the total amount of funding required for the project",
  "Please provide any additional relevant links, along with a brief description of each link",
  "Please upload the relevant deal document",
];

const FUND_OVERLAP_TITLE = normalizeLookup(
  "Please specify the total amount of funding required for the project",
);
const LINKS_OVERLAP_TITLE = normalizeLookup(
  "Please provide any additional relevant links, along with a brief description of each link",
);
const DECK_OVERLAP_TITLE = normalizeLookup(
  "Please upload the relevant deal document",
);

function normalizeLookup(title) {
  return String(title || "")
    .trim()
    .toLowerCase()
    .replace(/\.$/, "");
}

export const normalizeQuestionTitle = (title) => normalizeLookup(title);

export const isJvEnquiryOverlapQuestion = (questionTitle) => {
  const normalized = normalizeQuestionTitle(questionTitle);
  return JV_ENQUIRY_OVERLAP_QUESTION_TITLES.some(
    (t) => normalizeQuestionTitle(t) === normalized,
  );
};

/**
 * Skip an enquiry-overlap question only when autofill already has that value.
 * If amountToRaise is missing, keep the funding question so first deal can still capture it.
 */
export const shouldSkipJvEnquiryOverlapQuestion = (
  questionTitle,
  autofillData = null,
) => {
  if (!isJvEnquiryOverlapQuestion(questionTitle)) return false;
  if (!autofillData?.isFirstDeal) return false;

  const normalized = normalizeQuestionTitle(questionTitle);

  if (normalized === FUND_OVERLAP_TITLE || normalized === "fund required (₹)") {
    return (
      autofillData.fundRequired != null ||
      autofillData.amountToRaise != null
    );
  }

  if (normalized === LINKS_OVERLAP_TITLE) {
    return Boolean(autofillData.referenceLinks);
  }

  if (normalized === DECK_OVERLAP_TITLE) {
    return Boolean(autofillData.projectProposal || autofillData.pitchDeck);
  }

  return false;
};

/** JV tenure questions removed in favour of project duration. */
export const JV_DEPRECATED_TENURE_QUESTION_TITLES = [
  "What is the project tenure (in months)?",
  "Tenure (Months)",
];

export const isDeprecatedJvTenureQuestion = (questionTitle) => {
  const normalized = normalizeQuestionTitle(questionTitle);
  return JV_DEPRECATED_TENURE_QUESTION_TITLES.some(
    (title) => normalizeQuestionTitle(title) === normalized,
  );
};

export const getJvEnquiryOverlapQuestionIds = (
  categories = [],
  autofillData = null,
) => {
  const ids = new Set();
  categories.forEach((category) => {
    category?.questions?.forEach((question) => {
      const shouldSkip = autofillData
        ? shouldSkipJvEnquiryOverlapQuestion(question.questionTitle, autofillData)
        : isJvEnquiryOverlapQuestion(question.questionTitle);
      if (shouldSkip) {
        ids.add(question.questionId);
      }
    });
  });
  return ids;
};

export const getJvQuestionDedupeKey = (questionTitle) => {
  const normalized = normalizeQuestionTitle(questionTitle);
  const JV_TITLE_FIELD_KEYS = {
    "project name": "projectName",
    "please provide the name of the project": "projectName",
    "project description": "projectDescription",
    "please provide a brief description of the project": "projectDescription",
    "project duration (months)": "projectDuration",
    "what is the expected duration of the project?": "projectDuration",
    "project value (₹)": "projectValue",
    "please specify the project value": "projectValue",
    "please specify the project tenure in months": "projectValue",
    "fund required (₹)": "fundRequired",
    "please specify the total amount of funding required for the project": "fundRequired",
    "expected roi (%)": "expectedRoi",
    "what is the expected roi (%) for the project?": "expectedRoi",
    "profit share (%)": "profitShare",
    "what percentage of profit share are you offering to investors?": "profitShare",
    "investment structure": "investmentStructure",
    "please provide a description of the proposed investment structure": "investmentStructure",
    "revenue / profit sharing model": "profitSharingModel",
    "please describe the proposed revenue/profit-sharing model": "profitSharingModel",
    "risk factors (optional)": "riskFactors",
    "please provide details of any potential risk factors associated with the project (optional)": "riskFactors",
    "reference links (optional)": "referenceLinks",
    "please provide any additional relevant links, along with a brief description of each link": "referenceLinks",
    "project proposal": "projectProposal",
    "please upload the relevant deal document": "projectProposal",
    "financial projections": "financialProjections",
    "please upload the financial projections for the project": "financialProjections",
    "supporting documents (optional)": "supportingDocs",
    "please upload any additional supporting documents, if applicable (optional)": "supportingDocs",
  };

  return JV_TITLE_FIELD_KEYS[normalized] || normalized;
};

export const JV_DEFAULT_KYC_QUESTION_IDS = [
  "QBM1000002",
  "QBM1000015",
  "QBM1000014",
  "QBM1000043",
];

export const buildDefaultJvKycQuestionSelection = (
  mappedQuestions = [],
  mandatoryQuestionIds = JV_DEFAULT_KYC_QUESTION_IDS,
) => {
  const selectedQuestions = {};
  const mandatorySet = new Set(mandatoryQuestionIds);

  mappedQuestions.forEach((question) => {
    if (!question?.categoryId || !mandatorySet.has(question.questionId)) {
      return;
    }

    if (!selectedQuestions[question.categoryId]) {
      selectedQuestions[question.categoryId] = [];
    }

    if (!selectedQuestions[question.categoryId].includes(question.questionId)) {
      selectedQuestions[question.categoryId].push(question.questionId);
    }
  });

  return selectedQuestions;
};

export const dedupeQuestionIdsByTitle = (questionIds = [], categories = []) => {
  const questionById = new Map();
  categories.forEach((category) => {
    category?.questions?.forEach((question) => {
      questionById.set(question.questionId, question);
    });
  });

  const seenKeys = new Set();
  return questionIds.filter((questionId) => {
    const question = questionById.get(questionId);
    if (!question) {
      return true;
    }

    const dedupeKey = getJvQuestionDedupeKey(question.questionTitle);
    if (seenKeys.has(dedupeKey)) {
      return false;
    }

    seenKeys.add(dedupeKey);
    return true;
  });
};
